import { create } from 'zustand';
import { devtools, subscribeWithSelector } from 'zustand/middleware';
import { useShallow } from 'zustand/react/shallow';
import type { 
  PodcastSearchStore, 
  PodcastSearchResult, 
  PodcastSearchFilters,
  PodcastSearchResponse 
} from '@/types/podcast';

// Default filters
const defaultFilters: PodcastSearchFilters = {
  query: '',
  sortBy: 'relevance'
};

// Maximum search history items
const MAX_HISTORY_ITEMS = 10;

// Helper function to debounce API calls
let searchTimeout: NodeJS.Timeout | null = null;

// Helper function to perform the actual search API call
const performSearchAPI = async (
  query: string, 
  filters: PodcastSearchFilters, 
  page: number = 1,
  limit: number = 20
): Promise<PodcastSearchResponse> => {
  const searchParams = new URLSearchParams({
    q: query,
    page: page.toString(),
    limit: limit.toString(),
    ...(filters.category && { category: filters.category }),
    ...(filters.language && { language: filters.language }),
    ...(filters.country && { country: filters.country }),
    ...(filters.explicit !== undefined && { explicit: filters.explicit.toString() }),
    ...(filters.minSubscribers && { minSubscribers: filters.minSubscribers.toString() }),
    ...(filters.sortBy && { sortBy: filters.sortBy })
  });

  const response = await fetch(`/api/search/podcasts?${searchParams}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `Search failed with status ${response.status}`);
  }

  return response.json();
};

export const usePodcastSearchStore = create<PodcastSearchStore>()(
  devtools(
    subscribeWithSelector((set, get) => ({
      // Initial state
      query: '',
      filters: defaultFilters,
      results: [],
      currentPage: 1,
      totalCount: 0,
      hasMore: false,
      isLoading: false,
      isLoadingMore: false,
      error: null,
      searchHistory: [],
      isFiltersOpen: false,
      selectedPodcast: null,

      // Search actions
      setQuery: (query: string) => {
        const currentQuery = get().query;
        if (currentQuery === query) {
          return; // Prevent unnecessary updates
        }
        
        set({ query });
        
        // Clear existing timeout
        if (searchTimeout) {
          clearTimeout(searchTimeout);
          searchTimeout = null;
        }
        
        // Clear results if query is empty
        if (!query.trim()) {
          set({ results: [], totalCount: 0, hasMore: false, error: null });
        }
        // Note: We don't auto-search here anymore to prevent loops
        // Search will be triggered manually from components
      },

      setFilters: (newFilters: Partial<PodcastSearchFilters>) => {
        const currentFilters = get().filters;
        
        // Check if filters actually changed
        const hasChanges = Object.keys(newFilters).some(
          key => newFilters[key as keyof PodcastSearchFilters] !== currentFilters[key as keyof PodcastSearchFilters]
        );
        
        if (!hasChanges) return; // Prevent unnecessary updates
        
        const updatedFilters = { ...currentFilters, ...newFilters };
        set({ filters: updatedFilters });
        
        // Re-search with new filters if there's a query
        const { query } = get();
        if (query.trim()) {
          // Use timeout to prevent rapid successive filter changes
          if (searchTimeout) {
            clearTimeout(searchTimeout);
          }
          
          searchTimeout = setTimeout(() => {
            get().performSearch(query, true);
          }, 100);
        }
      },

      resetFilters: () => {
        const currentFilters = get().filters;
        
        // Check if filters are already at default
        const isAlreadyDefault = Object.keys(defaultFilters).every(
          key => currentFilters[key as keyof PodcastSearchFilters] === defaultFilters[key as keyof PodcastSearchFilters]
        );
        
        if (isAlreadyDefault) return; // Prevent unnecessary updates
        
        set({ filters: defaultFilters });
        
        // Re-search with reset filters if there's a query
        const { query } = get();
        if (query.trim()) {
          // Use timeout to prevent rapid successive calls
          if (searchTimeout) {
            clearTimeout(searchTimeout);
          }
          
          searchTimeout = setTimeout(() => {
            get().performSearch(query, true);
          }, 100);
        }
      },

      // Results actions
      setResults: (results: PodcastSearchResult[]) => set({ results }),

      appendResults: (newResults: PodcastSearchResult[]) => {
        const { results } = get();
        const combinedResults = [...results, ...newResults];
        // Remove duplicates based on ID
        const uniqueResults = combinedResults.filter((podcast, index, self) => 
          index === self.findIndex(p => p.id === podcast.id)
        );
        set({ results: uniqueResults });
      },

      clearResults: () => set({ 
        results: [], 
        totalCount: 0, 
        hasMore: false, 
        currentPage: 1,
        error: null 
      }),

      // Pagination actions
      setCurrentPage: (page: number) => set({ currentPage: page }),
      setTotalCount: (count: number) => set({ totalCount: count }),
      setHasMore: (hasMore: boolean) => set({ hasMore }),

      // Loading actions
      setIsLoading: (loading: boolean) => set({ isLoading: loading }),
      setIsLoadingMore: (loading: boolean) => set({ isLoadingMore: loading }),

      // Error actions
      setError: (error: string | null) => set({ error }),
      clearError: () => set({ error: null }),

      // History actions
      addToHistory: (query: string) => {
        if (!query.trim()) return;
        
        const { searchHistory } = get();
        const newHistory = [
          query,
          ...searchHistory.filter(item => item !== query)
        ].slice(0, MAX_HISTORY_ITEMS);
        
        set({ searchHistory: newHistory });
        
        // Persist to localStorage
        try {
          localStorage.setItem('podcast-search-history', JSON.stringify(newHistory));
        } catch (error) {
          console.warn('Failed to save search history to localStorage:', error);
        }
      },

      clearHistory: () => {
        set({ searchHistory: [] });
        try {
          localStorage.removeItem('podcast-search-history');
        } catch (error) {
          console.warn('Failed to clear search history from localStorage:', error);
        }
      },

      // UI actions
      setIsFiltersOpen: (open: boolean) => set({ isFiltersOpen: open }),
      setSelectedPodcast: (podcast: PodcastSearchResult | null) => set({ selectedPodcast: podcast }),

      // Complex actions
      performSearch: async (searchQuery?: string, resetPage: boolean = false) => {
        console.log('🚀 performSearch called with:', { searchQuery, resetPage });
        const { query, filters, currentPage, isLoading } = get();
        const searchTerm = searchQuery || query;
        
        console.log('🚀 searchTerm:', searchTerm, 'isLoading:', isLoading);
        
        if (!searchTerm.trim()) {
          console.log('🚀 Empty search term, clearing results');
          set({ results: [], totalCount: 0, hasMore: false, error: null });
          return;
        }

        // Prevent multiple simultaneous searches
        if (isLoading) {
          console.log('🚀 Already loading, skipping search');
          return;
        }

        const page = resetPage ? 1 : currentPage;
        console.log('🚀 Starting search with page:', page);
        
        try {
          set({ 
            isLoading: true, 
            error: null,
            ...(resetPage && { currentPage: 1 })
          });

          console.log('🚀 Calling performSearchAPI with:', { searchTerm, filters, page });
          const response = await performSearchAPI(searchTerm, filters, page);
          console.log('🚀 API response received:', response);
          
          // Check if the query is still the same (user might have changed it)
          const currentState = get();
          if (currentState.query !== searchTerm) {
            console.log('🚀 Query changed during search, ignoring response');
            return; // Ignore this response as the query has changed
          }
          
          if (resetPage) {
            console.log('🚀 Setting new results:', response.podcasts.length, 'podcasts');
            set({ 
              results: response.podcasts,
              totalCount: response.totalCount,
              hasMore: response.hasMore,
              currentPage: response.page,
              isLoading: false
            });
          } else {
            console.log('🚀 Appending results:', response.podcasts.length, 'podcasts');
            get().appendResults(response.podcasts);
            set({
              totalCount: response.totalCount,
              hasMore: response.hasMore,
              currentPage: response.page,
              isLoading: false
            });
          }

          // Add to search history
          get().addToHistory(searchTerm);

        } catch (error) {
          console.error('🚀 Search error:', error);
          set({ 
            error: error instanceof Error ? error.message : 'Search failed',
            isLoading: false,
            isLoadingMore: false
          });
        }
      },

      loadMore: async () => {
        const { hasMore, isLoadingMore, currentPage, query, filters } = get();
        
        if (!hasMore || isLoadingMore || !query.trim()) return;

        try {
          set({ isLoadingMore: true, error: null });
          
          const nextPage = currentPage + 1;
          const response = await performSearchAPI(query, filters, nextPage);
          
          get().appendResults(response.podcasts);
          set({
            totalCount: response.totalCount,
            hasMore: response.hasMore,
            currentPage: response.page,
            isLoadingMore: false
          });

        } catch (error) {
          console.error('Load more error:', error);
          set({ 
            error: error instanceof Error ? error.message : 'Failed to load more results',
            isLoadingMore: false
          });
        }
      },

      reset: () => {
        // Clear any pending timeouts
        if (searchTimeout) {
          clearTimeout(searchTimeout);
          searchTimeout = null;
        }
        
        set({
          query: '',
          filters: defaultFilters,
          results: [],
          currentPage: 1,
          totalCount: 0,
          hasMore: false,
          isLoading: false,
          isLoadingMore: false,
          error: null,
          isFiltersOpen: false,
          selectedPodcast: null,
        });
      },
    })),
    {
      name: 'podcast-search-store',
    }
  )
);

// Optimized selector hooks with shallow comparison to prevent unnecessary re-renders

export const usePodcastSearchResults = () => usePodcastSearchStore(useShallow((state) => ({
  results: state.results,
  totalCount: state.totalCount,
  hasMore: state.hasMore,
  isLoading: state.isLoading,
  isLoadingMore: state.isLoadingMore,
  error: state.error,
})));

export const usePodcastSearchQuery = () => usePodcastSearchStore(useShallow((state) => ({
  query: state.query,
  setQuery: state.setQuery,
})));

export const usePodcastSearchFilters = () => usePodcastSearchStore(useShallow((state) => ({
  filters: state.filters,
  setFilters: state.setFilters,
  resetFilters: state.resetFilters,
  isFiltersOpen: state.isFiltersOpen,
  setIsFiltersOpen: state.setIsFiltersOpen,
})));

export const usePodcastSearchHistory = () => usePodcastSearchStore(useShallow((state) => ({
  searchHistory: state.searchHistory,
  addToHistory: state.addToHistory,
  clearHistory: state.clearHistory,
})));

export const usePodcastSearchActions = () => usePodcastSearchStore(useShallow((state) => ({
  performSearch: state.performSearch,
  loadMore: state.loadMore,
  clearResults: state.clearResults,
  reset: state.reset,
})));

export const usePodcastSelection = () => usePodcastSearchStore(useShallow((state) => ({
  selectedPodcast: state.selectedPodcast,
  setSelectedPodcast: state.setSelectedPodcast,
})));

// Initialize search history from localStorage on client side
if (typeof window !== 'undefined') {
  try {
    const savedHistory = localStorage.getItem('podcast-search-history');
    if (savedHistory) {
      const history = JSON.parse(savedHistory);
      if (Array.isArray(history)) {
        usePodcastSearchStore.setState({ searchHistory: history });
      }
    }
  } catch (error) {
    console.warn('Failed to load search history from localStorage:', error);
  }
}